import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageMeta } from '@/hooks/usePageMeta';

const FORM_URL = 'https://readdy.ai/api/form/db7g9snidngsrf4ehkf0';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function AboutPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  usePageMeta(
    '关于我们 - 久久久精品影视资源聚合平台',
    '了解久久久精品的平台定位、内容聚合理念与联系方式，我们致力于为你提供便捷舒适的影视内容浏览体验。',
    '久久久精品,关于我们,影视聚合平台,影视资源',
  );

  const features = [
    { icon: 'ri-movie-2-line', title: t('aboutFeature1Title'), desc: t('aboutFeature1Desc') },
    { icon: 'ri-filter-3-line', title: t('aboutFeature2Title'), desc: t('aboutFeature2Desc') },
    { icon: 'ri-file-text-line', title: t('aboutFeature3Title'), desc: t('aboutFeature3Desc') },
  ];

  const stats = [
    { value: '24+', label: t('aboutStatWorks'), icon: 'ri-film-line' },
    { value: '3', label: t('aboutStatCategory'), icon: 'ri-apps-2-line' },
    { value: '50万+', label: t('aboutStatUsers'), icon: 'ri-team-line' },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const v = String(value).trim();
      if (v) payload.append(key, v);
    });

    setStatus('sending');
    setFormError('');
    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '';
      const ok = response.ok && parsed?.code === 'OK';
      if (!ok) {
        setStatus('error');
        setFormError(serverMsg || t('formError'));
        return;
      }
      setStatus('success');
      form.reset();
    } catch {
      setStatus('error');
      setFormError(t('formError'));
    }
  };

  return (
    <div className="jjj-fade-up">
      <div className="bg-primary-950">
        <div className="mx-auto max-w-page px-4 py-12 md:px-6 md:py-16">
          <nav className="mb-4 flex items-center gap-2 text-xs text-background-200/70">
            <Link to="/" className="transition hover:text-accent-300 cursor-pointer">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">{t('navAbout')}</span>
          </nav>
          <h1 className="font-heading text-3xl font-extrabold text-background-50 md:text-4xl">{t('aboutTitle')}</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-100/85 md:text-base">{t('aboutLead')}</p>
        </div>
      </div>

      <div className="mx-auto max-w-page px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {features.map((f) => (
            <div key={f.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className={`${f.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 rounded-lg bg-secondary-50 p-6 sm:grid-cols-3 md:p-8">
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500 text-accent-950">
                <i className={`${s.icon} text-2xl`}></i>
              </span>
              <div>
                <p className="font-heading text-2xl font-extrabold text-foreground-950">{s.value}</p>
                <p className="text-xs text-foreground-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.3fr] lg:gap-10">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">{t('contactTitle')}</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500">{t('contactDesc')}</p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700"><i className="ri-mail-line"></i></span>
                contact@jiujiujiu.com
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700"><i className="ri-phone-line"></i></span>
                400-000-9999
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700"><i className="ri-time-line"></i></span>
                周一至周五 09:00 - 18:00
              </li>
            </ul>
            <div className="mt-6 flex gap-2">
              {['ri-wechat-line', 'ri-weibo-line', 'ri-mail-line'].map((icon) => (
                <span key={icon} className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-foreground-600 transition hover:bg-accent-500 hover:text-accent-950 cursor-pointer">
                  <i className={`${icon} text-lg`}></i>
                </span>
              ))}
            </div>
          </div>

          <form
            data-readdy-form
            id="about-feedback-form"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-600">{t('formName')}</span>
                <input
                  name="name"
                  required
                  placeholder="请输入你的称呼"
                  className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-600">{t('formEmail')}</span>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </label>
            </div>
            <label className="mt-4 flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-600">{t('formSubject')}</span>
              <select
                name="subject"
                className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              >
                <option value="内容建议">内容建议</option>
                <option value="内容纠错">内容纠错</option>
                <option value="商务合作">商务合作</option>
                <option value="其他反馈">其他反馈</option>
              </select>
            </label>
            <label className="mt-4 flex flex-col gap-1.5">
              <span className="flex items-center justify-between text-xs font-medium text-foreground-600">
                <span>{t('formMessage')}</span>
                <span className="text-foreground-400">{t('formMessageHint')}</span>
              </span>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请输入你的反馈内容…"
                className="resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              ></textarea>
            </label>

            <input
              className="form-alt-field"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              placeholder="请勿填写"
            />

            {status === 'error' ? (
              <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-3.5 py-2.5 text-sm text-accent-800">
                <i className="ri-error-warning-line"></i>
                {formError}
              </p>
            ) : null}
            {status === 'success' ? (
              <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-100 px-3.5 py-2.5 text-sm text-secondary-900">
                <i className="ri-checkbox-circle-line"></i>
                {t('formSuccess')}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-60 cursor-pointer whitespace-nowrap"
            >
              <i className={status === 'sending' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
              {status === 'sending' ? t('formSending') : t('formSubmit')}
            </button>
            <p className="mt-3 text-center text-xs text-foreground-400">我们会在 1-2 个工作日内回复你的反馈</p>
          </form>
        </div>

        <div className="mt-14 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-shield-check-line text-accent-500"></i>
            {t('legalTitle')}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-500">{t('legalText')}</p>
        </div>
      </div>
    </div>
  );
}